import {gql} from '@apollo/client';
import {pick} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React from 'react';

import {CoreObjectId} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType, getObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {Permissions} from 'auth/PermissionConstants';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionUtil';
import PoliceableDAO from 'collaborator-components/PoliceableDAO';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OwnableInfo, UpdateOwnableParams} from 'create-components/OwnableConstants';
import {
  OwnableSettingsModalProps,
  OwnableSettingsWithCollaborationsModal,
} from 'create-components/OwnableSettingsModal';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {updateSkill} from 'llm/chat-library/skills/SkillsDAO';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface SkillSettingsModalProps {
  skillId: string;
  onRequestClose: () => void;
  onUpdate?: () => void;
}

interface AgentSkillSettingsQueryData {
  agentSkill:
    | {
        __typename: 'AgentSkillNode';
        id: string;
        name: string;
        ownerJson: JSONScalar;
        ownerInfoJson: JSONScalar;
        collaboratorsJson: Array<JSONScalar>;
        permissions: {
          __typename: 'PermissionsNode';
          admin: boolean;
          appendable: boolean;
          owner: boolean | null;
          readable: boolean;
          writable: boolean;
        };
      }
    | {
        __typename: 'InaccessibleInfoNode';
        invalidId: string;
        canRequestAccess: boolean;
      }
    | null;
}

interface AgentSkillSettingsQueryVariables {
  skillId: string;
}

const AGENT_SKILL_SETTINGS_QUERY: DocumentNode = gql`
  query AgentSkillSettingsQuery($skillId: StrictString!) @skipAudit @engComponent(name: LLM) {
    agentSkill(id: $skillId) {
      ... on OwnableInterface {
        ...OwnerAndCollaboratorInfoGraphQL
        ...HasOwnablePermissionsGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('OwnerAndCollaboratorInfo').fragment}
  ${getFragmentWrapper('HasOwnablePermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function SkillSettingsModalInner({
  skillId,
  ownableInfo,
  permissions,
  onRequestClose,
  onUpdate,
}: {
  skillId: string;
  ownableInfo: OwnableInfo;
  permissions: Permissions;
  onRequestClose: () => void;
  onUpdate?: () => void;
}): JSX.Element {
  async function handleSubmit(data: UpdateOwnableParams): Promise<void> {
    const updates: {name?: string; description?: string; owner?: string} = {
      name: data.name !== ownableInfo.name ? data.name : undefined,
      description: data.description !== ownableInfo.description ? data.description : undefined,
      owner: data.owner.handle !== ownableInfo.owner.handle ? data.owner.handle : undefined,
    };
    if (updates.name !== undefined || updates.description !== undefined || updates.owner !== undefined) {
      await updateSkill(skillId, updates);
    }

    if (data.collaborations != null) {
      const policeableType = getObjectType({id: skillId as CoreObjectId});
      await PoliceableDAO.updateCollaborations(
        skillId,
        convertToCollaborationCreateOrUpdates(data.collaborations),
        getApiBaseForObjectType(policeableType)
      );
    }

    onUpdate?.();
  }

  const ownableSettingsProps: OwnableSettingsModalProps = {
    ownableType: ModelTypeConstants.AGENT_SKILL,
    existingOwnable: ownableInfo,
    onRequestClose,
    navigateOnSuccess: false,
    doSubmit: handleSubmit,
    enableName: true,
    enableDescription: true,
    isReadOnly: !permissions.admin,
    ownableId: skillId,
  };

  return <OwnableSettingsWithCollaborationsModal {...ownableSettingsProps} />;
}

export default function SkillSettingsModal({
  skillId,
  onRequestClose,
  onUpdate,
}: SkillSettingsModalProps): JSX.Element {
  return (
    <BenchlingQuery<AgentSkillSettingsQueryData, AgentSkillSettingsQueryVariables>
      query={AGENT_SKILL_SETTINGS_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{skillId}}
    >
      {(data) => {
        if (data.agentSkill == null || isInaccessible(data.agentSkill)) {
          return <></>;
        }

        const skill = data.agentSkill;
        const ownableInfo: OwnableInfo = {
          id: skill.id,
          name: skill.name,
          owner: skill.ownerJson,
          ownerInfo: skill.ownerInfoJson,
          collaborators: skill.collaboratorsJson,
        };

        const skillPermissions: Permissions = {
          ...pick(skill.permissions, 'admin', 'appendable', 'readable', 'writable'),
          owner: skill.permissions.owner ?? undefined,
        };

        return (
          <SkillSettingsModalInner
            skillId={skillId}
            ownableInfo={ownableInfo}
            permissions={skillPermissions}
            onRequestClose={onRequestClose}
            onUpdate={onUpdate}
          />
        );
      }}
    </BenchlingQuery>
  );
}
